import { PropType, reactive } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import TempWorkerDocumentEntity from '@/entity/TempWorkerDocumentEntity';
import useTempWorkerDocumentService from '@/services/temp-worker/TempWorkerDocumentService';
import useDynamicImg from '@/services/DynamicImg';
import { cloneDeep } from 'lodash-es';

type Props = SetupProps<typeof UserProfileTempWorkerDocumentAsideUploadSetupService.props>

export default class UserProfileTempWorkerDocumentAsideUploadSetupService extends BaseSetupService<Props> {
	static readonly props = {
		tempWorkerDocumentEntity: {
			type: Object as PropType<TempWorkerDocumentEntity>,
			required: true as const,
		},
	};

	private _aside?: OrionAside;

	useTempWorkerDocumentService = useTempWorkerDocumentService();
	dynamicImg = useDynamicImg;

	private readonly state = reactive({
		showPreview: true,
		docRectoVerso: false,
		files: [] as File[],
		filesVerso: [] as File[],
		identityPieceType: undefined,
		displayErrorMessage: false,
	});

	get displayErrorMessage () { return this.state.displayErrorMessage; }
	set displayErrorMessage (val) { this.state.displayErrorMessage = val; }

	get identityPieceType () { return this.state.identityPieceType; }
	set identityPieceType (val) { this.state.identityPieceType = val; }

	get showPreview () { return this.state.showPreview; }
	set showPreview (val) { this.state.showPreview = val; }

	get docRectoVerso () { return this.state.docRectoVerso; }
	set docRectoVerso (val) { this.state.docRectoVerso = val; }

	get files () { return this.state.files; }
	set files (val) { this.state.files = val; }

	get filesVerso () { return this.state.filesVerso; }
	set filesVerso (val) { this.state.filesVerso = val; }

	get errorMessage () {
		if (this.props.tempWorkerDocumentEntity.isNew && !this.files?.length) {
			let msg = `Veuillez sélectionner un fichier`;
			if (this.docRectoVerso) msg += ` pour le recto`;
			if (this.docRectoVerso && !this.filesVerso?.length) msg += ` et un fichier pour le verso`;
			return msg;
		} else if (this.docRectoVerso && !this.filesVerso?.length) {
			return `Veuillez sélectionner un fichier pour le verso`;
		} else if (this.props.tempWorkerDocumentEntity.typeLabel === 'IdentityPiece'
		&& !Number.isFinite(this.props.tempWorkerDocumentEntity.identityPieceType)) {
			return 'Veuillez sélectionner un type de pièce d\'identité';
		} else if (this.props.tempWorkerDocumentEntity.typeLabel === 'DriverLicence' && !this.props.tempWorkerDocumentEntity.selectedDriverLicences?.length) {
			return `Veuillez sélectionner un ou plusieurs permis`;
		} else if (this.props.tempWorkerDocumentEntity.showDocumentTitle
			&& (typeof this.props.tempWorkerDocumentEntity.documentTitle !== 'string' || this.props.tempWorkerDocumentEntity.documentTitle.length === 0)) {
			return this.props.tempWorkerDocumentEntity.typeLabel === 'Diploma' ? 'Veuillez saisir un nom de diplôme' : 'Veuillez saisir un nom de fichier';
		} else {
			return null;
		}
	};

	get showAddOrUpdateButton () {
		if (this.props.tempWorkerDocumentEntity.isNew) {
			return !this.errorMessage;
		} else {
			return !this.errorMessage && (
				!!this.files.length
				|| !!this.filesVerso.length
				|| this.props.tempWorkerDocumentEntity.metaDataHasChanged
				|| this.props.tempWorkerDocumentEntity.expirationDateHasChanged
			);
		}
	}

	get fileTypes () {
		const fileTypes = ['image/jpeg', 'image/png'];
		if ((this.props.tempWorkerDocumentEntity.typeLabel !== 'IdentityPiece' && this.props.tempWorkerDocumentEntity.typeLabel !== 'DriverLicence')
		|| !this.docRectoVerso) fileTypes.push('application/pdf');
		return fileTypes;
	}

	get fileTypesToString () {
		return this.fileTypes.map(x => x.split('/')[1].toUpperCase()).join('/');
	}

	get labelFieldUpload () {
		let label = this.props.tempWorkerDocumentEntity.isNew ? `Glisser` : `Pour modifier votre document glisser`;
		if (this.docRectoVerso) {
			label += ` le <b class="text--info">recto</b> de votre document ici`;
		} else {
			label += ` un fichier ici`;
		}
		return label + ` au format ${this.fileTypesToString} (4 Mo max)`;
	}

	get labelFieldUploadVerso () {
		let label = this.props.tempWorkerDocumentEntity.isNew ? `Glisser` : `Pour modifier votre document glisser`;
		label += ` le <b class="text--info">verso</b> de votre document ici`;
		return label + ` au format ${this.fileTypesToString} (4 Mo max)`;
	}

	constructor (props?: Props, _aside?: OrionAside) {
		super(props);
		this._aside = _aside;
	}

	deleteDoc () {
		this._aside?.trigger('delete', this.props.tempWorkerDocumentEntity.id);
	}

	uploadDoc () {
		if (this.errorMessage) {
			this.displayErrorMessage = true;
		} else {
			this.displayErrorMessage = false;
			const dataToAdd = { ...this.props.tempWorkerDocumentEntity.dataForApiUpload };
			if (this.docRectoVerso) {
				dataToAdd.recto = this.files[0];
				dataToAdd.verso = this.filesVerso[0];
			} else {
				// Override type of angoisse
				dataToAdd[this.files[0].name as 'recto'] = this.files[0];
			}
			this._aside?.trigger('upload', dataToAdd);
		}
	}

	async updateDocAsync () {
		const shouldUpdateFile = !!this.files.length;
		const metaData = cloneDeep(this.props.tempWorkerDocumentEntity.data ?? {});

		if (shouldUpdateFile) {
			const updateTempWorkerDocumentModel = {
				id: this.props.tempWorkerDocumentEntity.id,
				recto: undefined as Undef<File>,
				verso: undefined as Undef<File>,
			};

			updateTempWorkerDocumentModel.recto = this.files[0];

			if (this.docRectoVerso && (!this.filesVerso || !this.filesVerso[0])) {
				this._aside?._loader()?.hide();
				throw `Veuillez sélectionner un fichier pour le verso.`;
			} else if (this.docRectoVerso) {
				updateTempWorkerDocumentModel.verso = this.filesVerso[0];
			}

			this._aside?.trigger('updateFile', updateTempWorkerDocumentModel);
		}

		if (this.props.tempWorkerDocumentEntity.expirationDateHasChanged
			&& this.props.tempWorkerDocumentEntity.documentExpiration?.id
			&& this.props.tempWorkerDocumentEntity.expirationDate) {
			this._aside?.trigger('updateExpirationDate', this.props.tempWorkerDocumentEntity.expirationDate);
		}

		if (this.props.tempWorkerDocumentEntity.metaDataHasChanged) {
			if (this.props.tempWorkerDocumentEntity.typeLabel === 'DriverLicence') {
				metaData.driverLicences = this.props.tempWorkerDocumentEntity.driverLicences.map(x => ({
					...x,
					obtainingDate: x.obtainingDate.toPost(),
				}));
			}
			this._aside?.trigger('updateMetaData', metaData);
		}
	}
}
